import React, { useEffect, useMemo, useState } from "react";
import {
  FileText,
  Users,
  Calendar,
  TrendingUp,
  Clock,
  CheckCircle,
  Activity,
  AlertCircle,
  RefreshCw,
  ShieldCheck,
  PenLine,
  ClipboardList,
} from "lucide-react";
import useApi from "../../../utils/api-manager/Helper/useApi";

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => safeNumber(value).toLocaleString("en-IN");

const formatDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const clampPercent = (value) => Math.min(100, Math.max(0, safeNumber(value)));

const KpiCard = ({ icon: Icon, label, value, helper, tone = "blue" }) => (
  <div className={`legdb-kpi-card legdb-tone-${tone}`}>
    <div className="legdb-kpi-icon">
      <Icon size={21} />
    </div>
    <div className="legdb-kpi-content">
      <div className="legdb-kpi-label">{label}</div>
      <div className="legdb-kpi-value">{value}</div>
      {helper && <div className="legdb-kpi-helper">{helper}</div>}
    </div>
  </div>
);

const InsightCard = ({ icon: Icon, title, value, note, tone = "blue", progress }) => (
  <div className={`legdb-insight-card legdb-tone-${tone}`}>
    <div className="legdb-insight-top">
      <div className="legdb-insight-icon">
        <Icon size={19} />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
    </div>
    <div className="legdb-insight-value">{value}</div>
    {typeof progress === "number" && (
      <div className="legdb-progress">
        <span style={{ width: `${clampPercent(progress)}%` }} />
      </div>
    )}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="legdb-section-header">
    <div className="legdb-section-icon">
      <Icon size={18} />
    </div>
    <div>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  </div>
);

const StatusPill = ({ children, tone = "blue" }) => (
  <span className={`legdb-pill legdb-pill-${tone}`}>{children}</span>
);

const EmptyState = ({ message }) => (
  <div className="legdb-empty-state">
    <FileText size={28} />
    <span>{message}</span>
  </div>
);

export default function Dashboard() {
  const { Get } = useApi();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await Get("dashboard");
      setDashboardData(response || {});
    } catch (fetchError) {
      console.error("Error fetching dashboard data:", fetchError);
      setError(fetchError?.message || "Failed to fetch legal dashboard data.");
      setDashboardData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const totalDocuments = safeNumber(dashboardData?.total_documents);
  const signedDocuments = safeNumber(dashboardData?.signed_documents);
  const pendingSignatures = safeNumber(dashboardData?.pending_signatures);
  const totalAssignments = safeNumber(dashboardData?.total_assignments);
  const completionRate = clampPercent(dashboardData?.completion_rate);
  const todayAssignmentsCreated = safeNumber(dashboardData?.today_activity?.assignments_created);
  const todayDocumentsSigned = safeNumber(dashboardData?.today_activity?.documents_signed);
  const popularDocuments = Array.isArray(dashboardData?.popular_documents)
    ? dashboardData.popular_documents
    : [];
  const recentActivity = Array.isArray(dashboardData?.recent_activity)
    ? dashboardData.recent_activity
    : [];

  const pendingRate = totalDocuments > 0 ? Math.round((pendingSignatures / totalDocuments) * 100) : 0;
  const signedRate = totalDocuments > 0 ? Math.round((signedDocuments / totalDocuments) * 100) : completionRate;
  const activityTodayTotal = todayAssignmentsCreated + todayDocumentsSigned;

  const currentDateLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const hasData = useMemo(
    () =>
      totalDocuments > 0 ||
      signedDocuments > 0 ||
      pendingSignatures > 0 ||
      totalAssignments > 0 ||
      popularDocuments.length > 0 ||
      recentActivity.length > 0 ||
      activityTodayTotal > 0,
    [
      totalDocuments,
      signedDocuments,
      pendingSignatures,
      totalAssignments,
      popularDocuments.length,
      recentActivity.length,
      activityTodayTotal,
    ]
  );

  if (loading) {
    return (
      <div className="responsive-box legdb-page">
        <style>{dashboardStyles}</style>
        <div className="legdb-loading">
          <div className="legdb-spinner" />
          <div>
            <h3>Loading Legal Dashboard</h3>
            <p>Preparing document, signature and assignment insights.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="responsive-box legdb-page">
        <style>{dashboardStyles}</style>
        <div className="legdb-empty-page legdb-error-page">
          <AlertCircle size={38} />
          <h2>Unable to Load Dashboard</h2>
          <p>{error}</p>
          <button type="button" onClick={fetchDashboardData} className="legdb-retry-btn">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="responsive-box legdb-page">
        <style>{dashboardStyles}</style>
        <div className="legdb-empty-page">
          <FileText size={38} />
          <h2>No Legal Dashboard Data Found</h2>
          <p>Create documents, assignments, or signature requests to start viewing legal management insights.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-box legdb-page">
      <style>{dashboardStyles}</style>

      <div className="legdb-shell">
        <section className="legdb-hero">
          <div>
            <div className="legdb-kicker">
              <span className="legdb-kicker-dot" />
              Legal Business View
            </div>
            <h1>Legal Management Dashboard</h1>
            <p>
              Simple executive view for legal documents, signatures, assignments and recent activity.
            </p>
            <div className="legdb-date-pill">
              <Calendar size={14} />
              {currentDateLabel}
            </div>
          </div>

          <div className="legdb-hero-kpis">
            <KpiCard
              icon={FileText}
              label="Total Documents"
              value={formatCount(totalDocuments)}
              helper="Legal documents in the system"
              tone="blue"
            />
            <KpiCard
              icon={CheckCircle}
              label="Signed Documents"
              value={formatCount(signedDocuments)}
              helper={`${signedRate}% completion health`}
              tone="green"
            />
            <KpiCard
              icon={Clock}
              label="Pending Signatures"
              value={formatCount(pendingSignatures)}
              helper="Awaiting action"
              tone={pendingSignatures > 0 ? "amber" : "green"}
            />
          </div>
        </section>

        <section className="legdb-insight-grid">
          <InsightCard
            icon={TrendingUp}
            title="Completion Rate"
            value={`${completionRate.toFixed(completionRate % 1 === 0 ? 0 : 1)}%`}
            note="Documents completed"
            progress={completionRate}
            tone={completionRate >= 80 ? "green" : completionRate >= 50 ? "blue" : "amber"}
          />
          <InsightCard
            icon={Users}
            title="Assignments"
            value={formatCount(totalAssignments)}
            note="Total document assignments"
            tone="purple"
          />
          <InsightCard
            icon={PenLine}
            title="Pending Rate"
            value={`${pendingRate}%`}
            note={`${formatCount(pendingSignatures)} pending signatures`}
            progress={pendingRate}
            tone={pendingRate > 30 ? "amber" : "blue"}
          />
          <InsightCard
            icon={Activity}
            title="Today's Activity"
            value={formatCount(activityTodayTotal)}
            note={`${formatCount(todayAssignmentsCreated)} assignments • ${formatCount(todayDocumentsSigned)} signed`}
            tone="green"
          />
        </section>

        <section className="legdb-focus-grid">
          <div className="legdb-panel">
            <SectionHeader
              icon={ShieldCheck}
              title="Document Health"
              subtitle="Quick view of signature and completion status"
            />

            <div className="legdb-health-card">
              <div className="legdb-health-main">
                <span>Completion</span>
                <strong>{completionRate.toFixed(completionRate % 1 === 0 ? 0 : 1)}%</strong>
              </div>
              <div className="legdb-progress legdb-health-progress">
                <span style={{ width: `${completionRate}%` }} />
              </div>

              <div className="legdb-snapshot-list">
                <div className="legdb-snapshot-row">
                  <span>Total Documents</span>
                  <strong>{formatCount(totalDocuments)}</strong>
                </div>
                <div className="legdb-snapshot-row">
                  <span>Signed Documents</span>
                  <strong className="legdb-green">{formatCount(signedDocuments)}</strong>
                </div>
                <div className="legdb-snapshot-row">
                  <span>Pending Signatures</span>
                  <strong className={pendingSignatures > 0 ? "legdb-amber-text" : "legdb-green"}>
                    {formatCount(pendingSignatures)}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <div className="legdb-panel">
            <SectionHeader
              icon={Activity}
              title="Today's Activity"
              subtitle="Daily legal workflow movement"
            />

            <div className="legdb-today-grid">
              <div className="legdb-today-card legdb-tone-purple">
                <div className="legdb-today-icon">
                  <ClipboardList size={22} />
                </div>
                <strong>{formatCount(todayAssignmentsCreated)}</strong>
                <span>Assignments Created</span>
              </div>

              <div className="legdb-today-card legdb-tone-green">
                <div className="legdb-today-icon">
                  <CheckCircle size={22} />
                </div>
                <strong>{formatCount(todayDocumentsSigned)}</strong>
                <span>Documents Signed</span>
              </div>
            </div>
          </div>
        </section>

        <section className="legdb-bottom-grid">
          <div className="legdb-panel">
            <SectionHeader
              icon={TrendingUp}
              title="Popular Documents"
              subtitle="Most-used legal document templates"
            />

            {popularDocuments.length > 0 ? (
              <div className="legdb-list">
                {popularDocuments.slice(0, 8).map((document, index) => (
                  <div className="legdb-list-row" key={`${document.title || "document"}-${index}`}>
                    <div className="legdb-list-left">
                      <div className="legdb-list-icon">
                        <FileText size={15} />
                      </div>
                      <div>
                        <strong>{document.title || "Untitled Document"}</strong>
                        <span>Document template</span>
                      </div>
                    </div>
                    <StatusPill tone="blue">
                      {formatCount(document.usage_count)} uses
                    </StatusPill>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState message="No popular documents yet." />
            )}
          </div>

          <div className="legdb-panel">
            <SectionHeader
              icon={Clock}
              title="Recent Activity"
              subtitle="Latest document assignment and signature updates"
            />

            {recentActivity.length > 0 ? (
              <div className="legdb-activity-list">
                {recentActivity.slice(0, 6).map((activity, index) => {
                  const status = String(activity.status || "").toLowerCase();
                  const isSigned = status === "signed";

                  return (
                    <div className="legdb-activity-row" key={`${activity.document__title || "activity"}-${index}`}>
                      <div className={`legdb-activity-dot ${isSigned ? "signed" : "pending"}`} />
                      <div className="legdb-activity-content">
                        <strong>{activity.document__title || "Document Activity"}</strong>
                        <span>
                          {formatDateTime(activity.created_at)} • {activity.status || "N/A"}
                        </span>
                      </div>
                      <StatusPill tone={isSigned ? "green" : "amber"}>
                        {isSigned ? "Signed" : activity.status || "Pending"}
                      </StatusPill>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState message="No recent activity available." />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

const dashboardStyles = `
  .legdb-page {
    min-height: 100vh;
    padding: 10px 12px 28px;
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 28%),
      radial-gradient(circle at top right, rgba(139, 92, 246, 0.08), transparent 28%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    color: #0f172a;
  }

  .legdb-shell {
    max-width: 1450px;
    margin: 0 auto;
  }

  .legdb-hero {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(440px, 0.85fr);
    gap: 18px;
    align-items: center;
    padding: 22px;
    margin-bottom: 14px;
    border-radius: 28px;
    color: #ffffff;
    background: linear-gradient(135deg, #020617 0%, #111827 56%, #3730a3 100%);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
  }

  .legdb-hero::after {
    content: "";
    position: absolute;
    width: 320px;
    height: 320px;
    right: -120px;
    top: -160px;
    border-radius: 999px;
    background: rgba(129, 140, 248, 0.16);
  }

  .legdb-hero > * {
    position: relative;
    z-index: 1;
  }

  .legdb-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 11px;
    border-radius: 999px;
    margin-bottom: 10px;
    font-size: 11px;
    font-weight: 950;
    color: #e0e7ff;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.22);
  }

  .legdb-kicker-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.16);
  }

  .legdb-hero h1 {
    margin: 0;
    font-size: 31px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.045em;
  }

  .legdb-hero p {
    max-width: 700px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.80);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.6;
  }

  .legdb-date-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 13px;
    padding: 7px 11px;
    border-radius: 999px;
    color: #c7d2fe;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 900;
  }

  .legdb-hero-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .legdb-kpi-card,
  .legdb-insight-card,
  .legdb-panel {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid #dbe3ec;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
  }

  .legdb-kpi-card {
    display: flex;
    align-items: center;
    gap: 13px;
    min-height: 82px;
    padding: 13px;
    border-radius: 20px;
  }

  .legdb-kpi-icon,
  .legdb-insight-icon,
  .legdb-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--legdb-accent);
    background: var(--legdb-soft);
    border: 1px solid var(--legdb-border);
    flex-shrink: 0;
  }

  .legdb-kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 15px;
  }

  .legdb-kpi-label {
    color: #475569;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .legdb-kpi-value {
    margin-top: 2px;
    color: #0f172a;
    font-size: 21px;
    font-weight: 950;
    letter-spacing: -0.035em;
  }

  .legdb-kpi-helper {
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .legdb-insight-grid,
  .legdb-focus-grid,
  .legdb-bottom-grid {
    display: grid;
    gap: 14px;
    margin-bottom: 14px;
  }

  .legdb-insight-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .legdb-focus-grid,
  .legdb-bottom-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .legdb-insight-card {
    border-radius: 22px;
    padding: 15px;
  }

  .legdb-insight-top {
    display: flex;
    gap: 11px;
    align-items: flex-start;
  }

  .legdb-insight-icon {
    width: 42px;
    height: 42px;
    border-radius: 15px;
  }

  .legdb-insight-card h3 {
    margin: 0;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .legdb-insight-card p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.45;
  }

  .legdb-insight-value {
    margin-top: 14px;
    color: #0f172a;
    font-size: 26px;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .legdb-progress {
    width: 100%;
    height: 8px;
    margin-top: 10px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
  }

  .legdb-progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--legdb-accent);
  }

  .legdb-panel {
    border-radius: 24px;
    padding: 16px;
    margin-bottom: 14px;
    overflow: hidden;
  }

  .legdb-section-header {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 14px;
  }

  .legdb-section-icon {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    --legdb-accent: #0f172a;
    --legdb-soft: #f8fafc;
    --legdb-border: #dbe3ec;
  }

  .legdb-section-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -0.025em;
  }

  .legdb-section-header p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .legdb-health-card {
    display: grid;
    gap: 12px;
  }

  .legdb-health-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 13px;
    border-radius: 18px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }

  .legdb-health-main span {
    color: #1d4ed8;
    font-size: 12px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .legdb-health-main strong {
    color: #1d4ed8;
    font-size: 26px;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .legdb-health-progress {
    margin-top: 0;
  }

  .legdb-snapshot-list {
    display: grid;
    gap: 10px;
  }

  .legdb-snapshot-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    padding: 11px 12px;
    border-radius: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }

  .legdb-snapshot-row span {
    color: #475569;
    font-size: 12px;
    font-weight: 850;
  }

  .legdb-snapshot-row strong {
    color: #0f172a;
    font-size: 15px;
    font-weight: 950;
  }

  .legdb-today-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .legdb-today-card {
    min-height: 175px;
    border-radius: 20px;
    padding: 16px;
    background: var(--legdb-soft);
    border: 1px solid var(--legdb-border);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 9px;
    text-align: center;
  }

  .legdb-today-icon {
    width: 48px;
    height: 48px;
    border-radius: 16px;
    color: var(--legdb-accent);
    background: #ffffff;
    border: 1px solid var(--legdb-border);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .legdb-today-card strong {
    color: #0f172a;
    font-size: 32px;
    font-weight: 950;
    line-height: 1;
  }

  .legdb-today-card span {
    color: #475569;
    font-size: 12px;
    font-weight: 850;
  }

  .legdb-list,
  .legdb-activity-list {
    display: grid;
    gap: 10px;
  }

  .legdb-list-row,
  .legdb-activity-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 12px;
    border-radius: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }

  .legdb-list-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .legdb-list-icon {
    width: 35px;
    height: 35px;
    border-radius: 12px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .legdb-list-left strong,
  .legdb-activity-content strong {
    display: block;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .legdb-list-left span,
  .legdb-activity-content span {
    display: block;
    margin-top: 2px;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .legdb-activity-row {
    justify-content: flex-start;
  }

  .legdb-activity-content {
    min-width: 0;
    flex: 1;
  }

  .legdb-activity-dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    flex-shrink: 0;
  }

  .legdb-activity-dot.signed {
    background: #10b981;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.14);
  }

  .legdb-activity-dot.pending {
    background: #f59e0b;
    box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.14);
  }

  .legdb-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    padding: 5px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .legdb-pill-blue {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .legdb-pill-green {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  }

  .legdb-pill-amber {
    background: #fffbeb;
    color: #b45309;
    border: 1px solid #fde68a;
  }

  .legdb-empty-state {
    min-height: 190px;
    border: 1px dashed #cbd5e1;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #64748b;
    font-size: 13px;
    font-weight: 850;
    background: #f8fafc;
  }

  .legdb-empty-page {
    min-height: 430px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    color: #0f172a;
  }

  .legdb-empty-page svg {
    color: #3730a3;
    margin-bottom: 14px;
  }

  .legdb-error-page svg {
    color: #dc2626;
  }

  .legdb-empty-page h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 950;
  }

  .legdb-empty-page p {
    max-width: 480px;
    margin: 8px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .legdb-retry-btn {
    margin-top: 14px;
    border: none;
    border-radius: 999px;
    height: 40px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #3730a3;
    color: #ffffff;
    font-size: 13px;
    font-weight: 950;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(55, 48, 163, 0.20);
  }

  .legdb-loading {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    color: #0f172a;
  }

  .legdb-loading h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 950;
  }

  .legdb-loading p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .legdb-spinner {
    width: 54px;
    height: 54px;
    border-radius: 999px;
    border: 5px solid #e0e7ff;
    border-top-color: #4f46e5;
    animation: legdb-spin 0.8s linear infinite;
  }

  @keyframes legdb-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .legdb-green {
    color: #059669 !important;
  }

  .legdb-amber-text {
    color: #d97706 !important;
  }

  .legdb-tone-blue {
    --legdb-accent: #2563eb;
    --legdb-soft: #eff6ff;
    --legdb-border: #bfdbfe;
  }

  .legdb-tone-green {
    --legdb-accent: #059669;
    --legdb-soft: #ecfdf5;
    --legdb-border: #a7f3d0;
  }

  .legdb-tone-purple {
    --legdb-accent: #7c3aed;
    --legdb-soft: #f3e8ff;
    --legdb-border: #ddd6fe;
  }

  .legdb-tone-amber {
    --legdb-accent: #d97706;
    --legdb-soft: #fffbeb;
    --legdb-border: #fde68a;
  }

  @media (max-width: 1200px) {
    .legdb-hero {
      grid-template-columns: 1fr;
    }

    .legdb-insight-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .legdb-focus-grid,
    .legdb-bottom-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .legdb-page {
      padding: 8px 8px 22px;
    }

    .legdb-hero {
      border-radius: 22px;
      padding: 18px;
    }

    .legdb-hero h1 {
      font-size: 25px;
    }

    .legdb-insight-grid,
    .legdb-today-grid {
      grid-template-columns: 1fr;
    }

    .legdb-panel,
    .legdb-insight-card {
      border-radius: 20px;
      padding: 14px;
    }

    .legdb-list-row,
    .legdb-activity-row {
      align-items: flex-start;
      flex-direction: column;
    }
  }
`;
